import type { CatalogProductDetail, CatalogVariant } from '@features/catalog/types/catalog.types';
import { CONTACT_INFO } from '@shared/constants/navigation';

export interface WhatsAppMessageOptions {
  product?: CatalogProductDetail;
  variant?: CatalogVariant | null;
  quantity?: number;
  customMessage?: string;
}

/**
 * Genera un mensaje personalizado de WhatsApp basado en el contexto del producto
 */
export function generateWhatsAppMessage(options: WhatsAppMessageOptions): string {
  const { product, variant, quantity = 1, customMessage } = options;

  // Si hay mensaje personalizado, usarlo
  if (customMessage) {
    return customMessage;
  }

  // Si no hay producto, mensaje genérico
  if (!product) {
    return 'Hola, me interesa conocer más sobre Reconócete';
  }

  // Construir mensaje con datos del producto
  // Usa asteriscos para negrita (*texto*) como en el checkout
  const parts: string[] = [];

  parts.push('Hola! Me interesa personalizar este producto:');
  parts.push('');
  parts.push(`*Producto:* ${product.name}`);

  // Agregar precio
  const price = variant?.price ?? product.base_price;
  parts.push(`*Precio:* $${price.toLocaleString('es-CO')} COP`);

  // Agregar atributos de la variante si existen
  if (variant?.attributes && Object.keys(variant.attributes).length > 0) {
    parts.push('');
    parts.push('*Opciones seleccionadas:*');
    Object.entries(variant.attributes).forEach(([key, value]) => {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      parts.push(`• ${capitalizedKey}: ${value}`);
    });
  }

  // Agregar cantidad
  if (quantity > 1) {
    parts.push('');
    parts.push(`*Cantidad:* ${quantity} unidades`);
  }

  // Agregar SKU si existe
  if (variant?.sku) {
    parts.push('');
    parts.push(`*SKU:* ${variant.sku}`);
  }

  parts.push('');
  parts.push('Podrian ayudarme con la personalizacion?');

  return parts.join('\n');
}

/**
 * Genera la URL completa de WhatsApp con el mensaje pre-llenado
 *
 * Nota: Usa un encoding especial que preserva los caracteres de formato de WhatsApp
 * (*_~) para que las negritas, cursivas y tachado funcionen correctamente.
 */
export function getWhatsAppUrl(message: string): string {
  // Encodear el mensaje pero preservar caracteres de formato de WhatsApp
  // Esto permite que *texto* se vea en negrita, _texto_ en cursiva, etc.
  const encoded = encodeURIComponent(message)
    .replace(/%2A/g, '*')  // Restaurar asteriscos para negrita
    .replace(/%5F/g, '_')  // Restaurar guiones bajos para cursiva
    .replace(/%7E/g, '~'); // Restaurar tildes para tachado

  return `https://wa.me/${CONTACT_INFO.whatsappNumber}?text=${encoded}`;
}
